/**
 * Detect whether the SPA is running inside the Tauri desktop wrapper vs a
 * normal browser tab. Phase D (2026-05-19) of the extraction-remediation plan.
 *
 * Used to:
 *   - Conditionally render `<DesktopTitleBar />` (web build keeps native chrome)
 *   - Conditionally surface the `<McpIntegration />` Settings panel (DESKTOP
 *     ONLY — the panel exposes IPC commands that don't exist in the browser,
 *     and the MCP integration is meaningless without the local stdio proxy)
 *   - Gate any other Tauri-only code path (clipboard helpers, file pickers, ...)
 *
 * The detection check looks for `__TAURI_INTERNALS__` on the global window —
 * Tauri injects this object into every webview at boot, and no normal browser
 * has it. The function is SSR-safe (returns false if `window` is undefined).
 */

export function isDesktopApp(): boolean {
  if (typeof window === 'undefined') return false;
  return '__TAURI_INTERNALS__' in window;
}

/**
 * True when running inside the **macOS** Tauri build (2026-06-12). The macOS window uses
 * `titleBarStyle: "Overlay"` (tauri.macos.conf.json) so the OS draws its native traffic-light
 * controls top-left — the app therefore must NOT draw its own min/max/close cluster, and must
 * inset its left chrome so it clears the traffic lights. Windows/Linux keep the frameless
 * custom controls. Detection is by user-agent (the webview UA carries the OS), gated on
 * `isDesktopApp()` so a Mac running the WEB build is unaffected.
 */
export function isMacDesktop(): boolean {
  if (!isDesktopApp()) return false;
  const ua = navigator.userAgent || '';
  // `navigator.userAgentData.platform` is the modern signal; fall back to the UA string
  // ("Macintosh"/"Mac OS X" appears in every macOS WKWebView UA).
  const uaDataPlatform =
    (navigator as { userAgentData?: { platform?: string } }).userAgentData?.platform ?? '';
  return /mac/i.test(uaDataPlatform) || /Macintosh|Mac OS X/.test(ua);
}
